/**
 * 지출 기타/사업소득 — 소득자 마스터 · 소득명세
 */
import {
  isBdApiEnabled,
  POC_BUCKET,
  pocParams,
  loadPocRows,
  savePocRows,
  enqueuePersist,
  cacheGet,
  cacheSet,
} from 'services/pocRowPersist';

export const INCOME_EARNER_STORAGE_PREFIX = 'erp.expense.incomeEarner';
export const INCOME_STATEMENT_STORAGE_PREFIX = 'erp.expense.incomeStatement';

/** 소득 구분 */
export const INCOME_KIND = {
  OTHER: 'OTHER',
  BUSINESS: 'BUSINESS',
};

export const INCOME_KIND_OPTIONS = [
  { value: INCOME_KIND.OTHER, label: '기타소득' },
  { value: INCOME_KIND.BUSINESS, label: '사업소득' },
];

/** 식별번호 유형 */
export const ID_TYPE = {
  RRN: 'RRN',
  BIZ: 'BIZ',
};

export const ID_TYPE_OPTIONS = [
  { value: ID_TYPE.RRN, label: '주민등록번호' },
  { value: ID_TYPE.BIZ, label: '사업자번호' },
];

/** 소득명세가 매달린 집행 단계 */
export const INCOME_REF_STAGE = {
  PROPOSAL: 'PROPOSAL',
  OBLIGATION: 'OBLIGATION',
  RESOLUTION: 'RESOLUTION',
  /** 지급 후 사후정산·소급 매핑 */
  POST: 'POST',
};

export const INCOME_REF_STAGE_OPTIONS = [
  { value: INCOME_REF_STAGE.PROPOSAL, label: '품의' },
  { value: INCOME_REF_STAGE.OBLIGATION, label: '원인행위' },
  { value: INCOME_REF_STAGE.RESOLUTION, label: '지출결의' },
  { value: INCOME_REF_STAGE.POST, label: '사후정산' },
];

/** 소득구분별 기본 필요경비율·원천세율 (PoC 기본값 · 행에서 변경 가능) */
export const INCOME_KIND_DEFAULTS = {
  [INCOME_KIND.OTHER]: { expenseRate: 60, taxRate: 20 },
  [INCOME_KIND.BUSINESS]: { expenseRate: 0, taxRate: 3 },
};

function labelOf(options, code) {
  return options.find((o) => o.value === code)?.label || code || '';
}

export function incomeKindLabel(code) {
  return labelOf(INCOME_KIND_OPTIONS, code);
}

export function idTypeLabel(code) {
  return labelOf(ID_TYPE_OPTIONS, code);
}

export function createUid(prefix = 'INC') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createEarnerUid() {
  return createUid('EARN');
}

export function createStatementUid() {
  return createUid('ISTM');
}

function earnerKey(dims = {}) {
  return [INCOME_EARNER_STORAGE_PREFIX, String(dims.tenantCode || '').trim()].join('|');
}

function statementKey(dims = {}) {
  return [
    INCOME_STATEMENT_STORAGE_PREFIX,
    String(dims.tenantCode || '').trim(),
    String(dims.fiscalYear || '').trim(),
  ].join('|');
}

function readRows(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch {
    return [];
  }
}

function writeRows(key, dims, rows) {
  localStorage.setItem(
    key,
    JSON.stringify({
      dims: { tenantCode: dims.tenantCode, fiscalYear: dims.fiscalYear },
      rows: rows || [],
      savedAt: new Date().toISOString(),
    })
  );
  return rows || [];
}

const earnerCache = new Map();
const statementCache = new Map();

function keepRows(rows) {
  return (rows || []).filter((r) => r && r.mod !== 'D').map((r) => ({ ...r, mod: '' }));
}

function nextSeqNo(rows = [], field = 'earnerCode') {
  const nums = (rows || [])
    .map((r) => Number(String(r[field] || '').replace(/\D/g, '')))
    .filter((n) => Number.isFinite(n) && n > 0);
  const max = nums.length ? Math.max(...nums) : 0;
  return String(max + 1).padStart(4, '0');
}

/** 지급액·세율로 필요경비·과세표준·원천세·지방세·실지급액 산출 */
export function calcIncomeTaxFields({
  payAmt = 0,
  expenseRate = 0,
  taxRate = 0,
} = {}) {
  const pay = Math.max(0, Number(payAmt) || 0);
  const expRate = Math.max(0, Number(expenseRate) || 0);
  const rate = Math.max(0, Number(taxRate) || 0);
  const expenseAmt = Math.floor((pay * expRate) / 100);
  const taxableAmt = Math.max(0, pay - expenseAmt);
  const incomeTax = Math.floor((taxableAmt * rate) / 100);
  const localTax = Math.floor(incomeTax / 10);
  const netPayAmt = pay - incomeTax - localTax;
  return { expenseAmt, taxableAmt, incomeTax, localTax, netPayAmt };
}

// ─── 소득자 마스터 ───────────────────────────────────────────────

export function emptyIncomeEarner(partial = {}) {
  const kind = partial.incomeKind || INCOME_KIND.OTHER;
  const defaults = INCOME_KIND_DEFAULTS[kind] || INCOME_KIND_DEFAULTS[INCOME_KIND.OTHER];
  return {
    earnerUid: '',
    earnerCode: '',
    earnerName: '',
    incomeKind: kind,
    idType: ID_TYPE.RRN,
    idNo: '',
    bankName: '',
    accountNo: '',
    accountHolder: '',
    defaultExpenseRate: defaults.expenseRate,
    defaultTaxRate: defaults.taxRate,
    phone: '',
    address: '',
    remark: '',
    useYn: 'Y',
    mod: '',
    ...partial,
  };
}

export function loadIncomeEarners(dims) {
  const key = earnerKey(dims);
  const cached = cacheGet(earnerCache, key);
  if (cached) return cached;
  if (isBdApiEnabled()) return [];
  const rows = readRows(key);
  cacheSet(earnerCache, key, rows);
  return rows;
}

export async function fetchIncomeEarners(dims) {
  const key = earnerKey(dims);
  if (isBdApiEnabled()) {
    return loadPocRows({
      bucket: POC_BUCKET.INCOME_EARNER,
      params: pocParams({ tenantCode: dims.tenantCode }),
      cache: earnerCache,
      cacheKey: key,
      uidKey: 'earnerUid',
    });
  }
  return loadIncomeEarners(dims);
}

export function saveIncomeEarners(dims, rows) {
  const key = earnerKey(dims);
  const kept = keepRows(rows);
  cacheSet(earnerCache, key, kept);
  if (isBdApiEnabled()) {
    enqueuePersist(key, () =>
      savePocRows({
        bucket: POC_BUCKET.INCOME_EARNER,
        params: pocParams({ tenantCode: dims.tenantCode }),
        cache: earnerCache,
        cacheKey: key,
        uidKey: 'earnerUid',
        nextRows: kept,
        ensureUid: () => createEarnerUid(),
      })
    );
    return kept;
  }
  return writeRows(key, dims, kept);
}

export function nextEarnerCode(rows = []) {
  return nextSeqNo(rows, 'earnerCode');
}

export function filterIncomeEarners(rows = [], filter = {}) {
  let list = (rows || []).filter((r) => r.mod !== 'D');
  const useYn = String(filter.useYn || '').trim();
  if (useYn) list = list.filter((r) => r.useYn === useYn);
  const kind = String(filter.incomeKind || '').trim();
  if (kind) list = list.filter((r) => r.incomeKind === kind);
  const q = String(filter.keyword || '').trim().toLowerCase();
  if (q) {
    list = list.filter((r) => {
      const hay = [r.earnerCode, r.earnerName, r.idNo, r.remark]
        .map((s) => String(s || '').toLowerCase())
        .join(' ');
      return hay.includes(q);
    });
  }
  return list;
}

/** 기관별 샘플 소득자 시드 (최초 1회) */
export function ensureIncomeEarnerSeed(dims = {}) {
  const existing = loadIncomeEarners(dims);
  if ((existing || []).some((r) => r.mod !== 'D')) return existing;

  const seed = [
    emptyIncomeEarner({
      earnerUid: createEarnerUid(),
      earnerCode: '0001',
      earnerName: '김상담',
      incomeKind: INCOME_KIND.OTHER,
      idType: ID_TYPE.RRN,
      idNo: '800101-1******',
      bankName: '국민은행',
      accountNo: '123-45-678901',
      accountHolder: '김상담',
      remark: '상담사 교육 강사(박사)',
      useYn: 'Y',
      mod: 'A',
    }),
    emptyIncomeEarner({
      earnerUid: createEarnerUid(),
      earnerCode: '0002',
      earnerName: '이자문',
      incomeKind: INCOME_KIND.BUSINESS,
      idType: ID_TYPE.BIZ,
      idNo: '123-45-67890',
      bankName: '신한은행',
      accountNo: '110-222-333444',
      accountHolder: '이자문',
      remark: '아동권리 자문(석사)',
      useYn: 'Y',
      mod: 'A',
    }),
  ].map((r) => {
    const d = INCOME_KIND_DEFAULTS[r.incomeKind] || {};
    return {
      ...r,
      defaultExpenseRate: d.expenseRate,
      defaultTaxRate: d.taxRate,
    };
  });

  return saveIncomeEarners(dims, seed);
}

// ─── 소득명세 (문서 연결) ────────────────────────────────────────

export function emptyIncomeStatement(partial = {}) {
  const kind = partial.incomeKind || INCOME_KIND.OTHER;
  const defaults = INCOME_KIND_DEFAULTS[kind] || INCOME_KIND_DEFAULTS[INCOME_KIND.OTHER];
  const base = {
    statementUid: '',
    fiscalYear: '',
    refStage: INCOME_REF_STAGE.PROPOSAL,
    proposalUid: '',
    obligationUid: '',
    resolutionUid: '',
    /** 결의번호(표시·감사). 저장·점유 시 채움 */
    resolutionNo: '',
    /** type1 연결 임시점유 */
    draftSessionUid: '',
    earnerUid: '',
    earnerCode: '',
    earnerName: '',
    incomeKind: kind,
    idType: ID_TYPE.RRN,
    idNo: '',
    payAmt: 0,
    expenseRate: defaults.expenseRate,
    expenseAmt: 0,
    taxableAmt: 0,
    taxRate: defaults.taxRate,
    incomeTax: 0,
    localTax: 0,
    netPayAmt: 0,
    payDate: '',
    bankName: '',
    accountNo: '',
    remark: '',
    mod: '',
    ...partial,
  };
  return { ...base, ...calcIncomeTaxFields(base) };
}

export function loadIncomeStatements(dims) {
  const key = statementKey(dims);
  const cached = cacheGet(statementCache, key);
  if (cached) return cached;
  if (isBdApiEnabled()) return [];
  const rows = readRows(key);
  cacheSet(statementCache, key, rows);
  return rows;
}

export async function fetchIncomeStatements(dims) {
  const key = statementKey(dims);
  if (isBdApiEnabled()) {
    return loadPocRows({
      bucket: POC_BUCKET.INCOME_STMT,
      params: pocParams(dims),
      cache: statementCache,
      cacheKey: key,
      uidKey: 'statementUid',
    });
  }
  return loadIncomeStatements(dims);
}

export function saveIncomeStatements(dims, rows) {
  const key = statementKey(dims);
  const kept = keepRows(rows);
  cacheSet(statementCache, key, kept);
  if (isBdApiEnabled()) {
    enqueuePersist(key, () =>
      savePocRows({
        bucket: POC_BUCKET.INCOME_STMT,
        params: pocParams(dims),
        cache: statementCache,
        cacheKey: key,
        uidKey: 'statementUid',
        nextRows: kept,
        ensureUid: () => createStatementUid(),
        parentUidOf: (r) => r.earnerUid,
      })
    );
    return kept;
  }
  return writeRows(key, dims, kept);
}

/** 품의 UID 기준 명세 (삭제 제외) */
export function loadStatementsByProposal(dims, proposalUid) {
  const uid = String(proposalUid || '').trim();
  if (!uid) return [];
  return loadIncomeStatements(dims).filter(
    (r) => r.proposalUid === uid && r.mod !== 'D'
  );
}

/**
 * 품의에 매달린 명세만 교체 저장.
 * 동일 FY·tenant의 다른 문서 명세는 유지.
 */
export function saveStatementsForProposal(dims, proposalUid, rows = []) {
  const uid = String(proposalUid || '').trim();
  if (!uid) return loadIncomeStatements(dims);

  const all = loadIncomeStatements(dims);
  const others = (all || []).filter((r) => r.proposalUid !== uid);
  const next = (rows || []).map((r) => {
    const withUid = {
      ...emptyIncomeStatement(r),
      ...r,
      proposalUid: uid,
      fiscalYear: dims.fiscalYear || r.fiscalYear || '',
      refStage: r.refStage || INCOME_REF_STAGE.PROPOSAL,
      statementUid: r.statementUid || createStatementUid(),
    };
    const calc = calcIncomeTaxFields(withUid);
    // 저장 완료 행은 변경표시 초기화 (팝업 재오픈·닫기 확인용)
    return { ...withUid, ...calc, mod: '' };
  });
  return saveIncomeStatements(dims, [...others, ...next]);
}

/** 소득자 마스터 행 → 명세 행 기본값 */
export function statementFromEarner(earner, partial = {}) {
  const kind = earner?.incomeKind || INCOME_KIND.OTHER;
  const defaults = INCOME_KIND_DEFAULTS[kind] || INCOME_KIND_DEFAULTS[INCOME_KIND.OTHER];
  return emptyIncomeStatement({
    earnerUid: earner?.earnerUid || '',
    earnerCode: earner?.earnerCode || '',
    earnerName: earner?.earnerName || '',
    incomeKind: kind,
    idType: earner?.idType || ID_TYPE.RRN,
    idNo: earner?.idNo || '',
    bankName: earner?.bankName || '',
    accountNo: earner?.accountNo || '',
    expenseRate: earner?.defaultExpenseRate ?? defaults.expenseRate,
    taxRate: earner?.defaultTaxRate ?? defaults.taxRate,
    ...partial,
  });
}

/** 미결 소득명세 (resolutionUid·draftSessionUid 없음 · 삭제 제외) */
export function filterOpenStatements(rows = []) {
  return (rows || []).filter(
    (r) =>
      r.mod !== 'D' &&
      !String(r.resolutionUid || '').trim() &&
      !String(r.draftSessionUid || '').trim()
  );
}

/** 결의 UID 기준 명세 */
export function loadStatementsByResolution(dims, resolutionUid) {
  const uid = String(resolutionUid || '').trim();
  if (!uid) return [];
  return loadIncomeStatements(dims).filter(
    (r) => String(r.resolutionUid || '').trim() === uid && r.mod !== 'D'
  );
}

/** type1 임시세션 기준 명세 */
export function loadStatementsByDraft(dims, draftSessionUid) {
  const uid = String(draftSessionUid || '').trim();
  if (!uid) return [];
  return loadIncomeStatements(dims).filter(
    (r) => String(r.draftSessionUid || '').trim() === uid && r.mod !== 'D'
  );
}

/**
 * 결의에 매달린 명세만 교체 저장.
 * 동일 FY·tenant의 다른 문서 명세는 유지.
 */
export function saveStatementsForResolution(dims, resolutionUid, rows = [], resolutionNo = '') {
  const uid = String(resolutionUid || '').trim();
  if (!uid) return loadIncomeStatements(dims);

  const resNo = String(resolutionNo || '').trim();
  const all = loadIncomeStatements(dims);
  const others = (all || []).filter((r) => String(r.resolutionUid || '').trim() !== uid);
  const next = (rows || []).map((r) => {
    const withUid = {
      ...emptyIncomeStatement(r),
      ...r,
      resolutionUid: uid,
      resolutionNo: resNo || String(r.resolutionNo || '').trim(),
      draftSessionUid: '',
      fiscalYear: dims.fiscalYear || r.fiscalYear || '',
      refStage: INCOME_REF_STAGE.RESOLUTION,
      statementUid: r.statementUid || createStatementUid(),
    };
    const calc = calcIncomeTaxFields(withUid);
    return { ...withUid, ...calc, mod: '' };
  });
  return saveIncomeStatements(dims, [...others, ...next]);
}

/** type1 임시세션에 매달린 명세 교체 저장 */
export function saveStatementsForDraft(dims, draftSessionUid, rows = []) {
  const uid = String(draftSessionUid || '').trim();
  if (!uid) return loadIncomeStatements(dims);
  const all = loadIncomeStatements(dims);
  const others = (all || []).filter((r) => String(r.draftSessionUid || '').trim() !== uid);
  const next = (rows || []).map((r) => {
    const withUid = {
      ...emptyIncomeStatement(r),
      ...r,
      draftSessionUid: uid,
      resolutionUid: '',
      resolutionNo: '',
      fiscalYear: dims.fiscalYear || r.fiscalYear || '',
      refStage: INCOME_REF_STAGE.RESOLUTION,
      statementUid: r.statementUid || createStatementUid(),
    };
    const calc = calcIncomeTaxFields(withUid);
    return { ...withUid, ...calc, mod: '' };
  });
  return saveIncomeStatements(dims, [...others, ...next]);
}

/**
 * 저장 시 statementUid 집합으로 해당 결의 연결 재동기화
 * - 집합 안: resolutionUid/No 기록
 * - 같은 결의인데 집합 밖: 해제(미결 복귀)
 */
export function syncStatementsForResolution(
  dims,
  { resolutionUid, resolutionNo = '', statementUids = [] } = {}
) {
  const resUid = String(resolutionUid || '').trim();
  if (!resUid) return loadIncomeStatements(dims);
  const keep = new Set((statementUids || []).map(String).filter(Boolean));
  const resNo = String(resolutionNo || '').trim();
  const all = loadIncomeStatements(dims);
  const next = all.map((r) => {
    const stUid = String(r.statementUid || '');
    const linkedHere = String(r.resolutionUid || '').trim() === resUid;
    if (keep.has(stUid)) {
      return {
        ...r,
        resolutionUid: resUid,
        resolutionNo: resNo,
        refStage: INCOME_REF_STAGE.RESOLUTION,
        mod: r.mod === 'A' ? 'A' : 'U',
      };
    }
    if (linkedHere) {
      return {
        ...r,
        resolutionUid: '',
        resolutionNo: '',
        mod: r.mod === 'A' ? 'A' : 'U',
      };
    }
    return r;
  });
  saveIncomeStatements(dims, next);
  return next;
}

/** 결의 삭제·취소 시 해당 결의에 묶인 소득명세 전부 해제 */
export function releaseStatementsByResolution(dims, resolutionUid) {
  const resUid = String(resolutionUid || '').trim();
  if (!resUid) return loadIncomeStatements(dims);
  const all = loadIncomeStatements(dims);
  const next = all.map((r) => {
    if (String(r.resolutionUid || '').trim() !== resUid) return r;
    return {
      ...r,
      resolutionUid: '',
      resolutionNo: '',
      draftSessionUid: '',
      mod: r.mod === 'A' ? 'A' : 'U',
    };
  });
  saveIncomeStatements(dims, next);
  return next;
}

/** type1 연결 — 소득명세 임시점유 */
export function occupyStatementsDraft(dims, statementUids, draftSessionUid) {
  const uidSet = new Set((statementUids || []).map(String).filter(Boolean));
  const draftUid = String(draftSessionUid || '').trim();
  if (!uidSet.size || !draftUid) return loadIncomeStatements(dims);
  const all = loadIncomeStatements(dims);
  const next = all.map((r) => {
    if (!uidSet.has(String(r.statementUid))) return r;
    return {
      ...r,
      draftSessionUid: draftUid,
      resolutionUid: '',
      resolutionNo: '',
      refStage: INCOME_REF_STAGE.RESOLUTION,
      mod: r.mod === 'A' ? 'A' : 'U',
    };
  });
  saveIncomeStatements(dims, next);
  return next;
}

export function releaseStatementsDraft(dims, draftSessionUid) {
  const draftUid = String(draftSessionUid || '').trim();
  if (!draftUid) return loadIncomeStatements(dims);
  const all = loadIncomeStatements(dims);
  const next = all.map((r) => {
    if (String(r.draftSessionUid || '').trim() !== draftUid) return r;
    return { ...r, draftSessionUid: '', mod: r.mod === 'A' ? 'A' : 'U' };
  });
  saveIncomeStatements(dims, next);
  return next;
}

export function promoteStatementsDraftToResolution(
  dims,
  { draftSessionUid, resolutionUid, resolutionNo = '', statementUids = [] } = {}
) {
  const draftUid = String(draftSessionUid || '').trim();
  const resUid = String(resolutionUid || '').trim();
  if (!draftUid || !resUid) return loadIncomeStatements(dims);
  const keep = new Set((statementUids || []).map(String).filter(Boolean));
  const resNo = String(resolutionNo || '').trim();
  const all = loadIncomeStatements(dims);
  const next = all.map((r) => {
    const stUid = String(r.statementUid || '');
    if (keep.has(stUid)) {
      return {
        ...r,
        draftSessionUid: '',
        resolutionUid: resUid,
        resolutionNo: resNo,
        refStage: INCOME_REF_STAGE.RESOLUTION,
        mod: r.mod === 'A' ? 'A' : 'U',
      };
    }
    if (String(r.draftSessionUid || '').trim() === draftUid) {
      return { ...r, draftSessionUid: '', mod: r.mod === 'A' ? 'A' : 'U' };
    }
    return r;
  });
  saveIncomeStatements(dims, next);
  return next;
}

/** 소득명세 → 결의 채주(디테일) 행 */
export function detailFromIncomeStatement(st, resolutionUid = '') {
  const s = st || {};
  return {
    detailUid: '',
    resolutionUid: resolutionUid || '',
    vendorCode: s.earnerCode || '',
    vendorName: s.earnerName || '',
    evidenceType: incomeKindLabel(s.incomeKind) || '소득명세',
    evidenceNo: s.statementUid || '',
    statementUid: s.statementUid || '',
    evidenceUid: '',
    vatYn: 'N',
    vatAmt: 0,
    accountDr: '',
    accountCr: '',
    bankName: s.bankName || '',
    accountNo: s.accountNo || '',
    cardNo: '',
    amount: Number(s.netPayAmt ?? s.payAmt) || 0,
    remark: s.remark || s.earnerName || '',
    mod: 'A',
  };
}
